import { Injectable } from '@angular/core';
import { StateService } from 'flux-core';
import { AbstractShapeView, DiagramRenderView } from 'flux-diagram-composer';
import { last, map, mapTo, mergeMap, switchMap } from 'rxjs/operators';
import { DiagramLocatorLocator } from './locator/diagram-locator-locator';
import { ShapeView } from '../shape/view/shape-view';
import { ImageView } from '../shape/view/image-view';
import { ConnectorView } from '../shape/view/connector-view';
import { from, Observable, of } from 'rxjs';
import { values } from 'lodash';
import { RetinaStage } from '../../framework/easeljs/retina-stage';
import { ShapeDisplayView } from '../shape/view/shape-display-view';
import { TextView } from '../shape/view/text/text-view';
import { ShapeTextEditorPositionService } from '../ui/text-editor/shape-text-editor-position.svc';
import { TiptapTextView } from '../../framework/easeljs/tiptap-text-view';
import { TiptapShapeDomView } from '../shape/view/text/tiptap-shape-dom-view';
import { fromPromise } from 'rxjs/internal-compatibility';
import { Bitmap } from '@creately/createjs-module';


@Injectable()
export class DiagramScreenshotSvc extends DiagramRenderView {
    constructor(
        protected state: StateService<any, any>,
        protected ll: DiagramLocatorLocator,
        protected shapeTextEditorPositionSvc: ShapeTextEditorPositionService,
    ) {
        super( state );

        TiptapTextView.state = this.state;
        TiptapShapeDomView.state = this.state;
        TiptapShapeDomView.shapeTextEditorPositionSvc = this.shapeTextEditorPositionSvc;
    }

    /**
     * Generate a snapshot of the diagram
     * @param shapeIds Array of shape IDs to include in the snapshot
     * @param ignoreTexts Whether to ignore text elements
     * @param quality Snapshot quality (scale factor)
     * @returns Promise resolving to a base64 image
     */
    async generate(
        shapeIds?: string[], 
        type: string = 'image/png',
        quality: number = 2,
        padding: number = 20,
        ignoreTexts: boolean = false,
        addWatermark: boolean = false,
        wait: boolean = true, // wait until tipta text views are rendeered
    ): Promise<string> {
        const locator = this.ll.forCurrent( false );
        return locator.getDiagramOnce().pipe(
            switchMap( diagram => fromPromise( addWatermark ? this.getWatermark() : Promise.resolve( null )).pipe(
                map( wm => ({ diagram, watermark: wm }))
            )),
            switchMap( v => {
                const diagram = v.diagram;
                const watermark = v.watermark;

                const bounds = this.state.get( 'Selected' ).length === 0
                            ? diagram.getBounds() : diagram.getBounds( this.state.get( 'Selected' ));
                const width = bounds.width + ( padding * 2 );
                const height = bounds.height + ( padding * 2 );
                return this.populateDiagram( diagram, shapeIds ).pipe(
                    mapTo({ x: bounds.x, y: bounds.y,  width, height, watermark }),
                );
            }),
            switchMap(({ x, y, width, height, watermark }) => {
                const children = this.getAllShapeData();
                if ( !wait ) {
                    return of( { x, y, width, height, watermark, children });
                }
                const promises = [];
                children.forEach( child =>  {
                    ( child.children || []).forEach( c => {
                        if ( c.drawPromise ) {
                            promises.push( c.drawPromise );
                        }
                    });
                });
                return fromPromise(
                    Promise.all( promises ).then(() => ({ x, y, width, height, watermark, children })));
            }),
            map(({ x, y, width, height, watermark, children }) => {
                const el = document.createElement( 'div' );
                el.id = 'diagram-screenshot';
                el.style.position = 'absolute';
                el.style.top = '0';
                el.style.left = '0';
                el.style.width = '100%';
                el.style.height = '100%';
                el.style.overflow = 'hidden';
                el.style.zIndex = '-10000';
                const canvas = document.createElement( 'canvas' );
                canvas.id = 'diagram-screenshot-canvas';
                el.appendChild( canvas );
                document.body.appendChild( el );
                const stage = new RetinaStage( canvas );

                children
                .forEach(( sv: ShapeDisplayView ) =>  {
                    if ( ignoreTexts ) {
                        sv.children?.forEach( c => {
                            if ( c instanceof TextView ) {
                                c.alpha = 0;
                            }
                        });
                    }
                    stage.addChild( sv );
                });

                if ( watermark ) {
                    this.positionWatermark( watermark, { width: width - ( padding * 2 ), height: height - ( padding * 2 ), x, y });
                    stage.addChild( watermark );
                }

                stage.cache( x - padding, y - padding, width, height, quality );
                let data;
                if ( type === 'image/jpeg' ) {
                    const cachedCanvas = ( stage.cacheCanvas as HTMLCanvasElement );
                    const clonedCanvas = cachedCanvas.cloneNode( true ) as HTMLCanvasElement;
                    const cloneContext = clonedCanvas.getContext( '2d' );
                    cloneContext.fillStyle = '#FFF';
                    cloneContext.fillRect( 0, 0, clonedCanvas.width, clonedCanvas.height );
                    cloneContext.drawImage( cachedCanvas, 0, 0 );
                    data = clonedCanvas.toDataURL( 'image/jpeg', 1 );
                } else {
                    data = stage.bitmapCache.getCacheDataURL();
                }
                canvas.remove();
                document.body.removeChild( el );
                this.destroy();
                return data;
            })
        ).toPromise();
    }

    public populateDiagram( diagram: any = {}, shapeIds = []): Observable<unknown> {
        return from( Object.values( diagram.shapes ).filter(( model: any )=>
            shapeIds.length ? shapeIds.includes( model.id ) : true
        )).pipe(
            mergeMap(( model: any )=> this.handleCreate( model )),
            last(),
        );
    }

    public getAllShapeData( shapeIds?: string[]): any {
        const shapes: AbstractShapeView[] =
                shapeIds ? this.getShapeViewsByShapeIds( shapeIds ) : values( this.getShapeViews());
        // Sort the shapes by zindex to make sure they are in correct order
        shapes.sort(( s1, s2 ) => s1.model.zIndex - s2.model.zIndex );
        const views = []
        for ( const shape of shapes ) {
            views.push( shape.renderedShape );
        }
        return views;
    }

    protected get locator() {
        return this.ll.forCurrentObserver( true );
    }

    /**
     * The associated shape class as required by
     * Diagram Composer
     * @override
     */
    protected get shapeViewType(): typeof ShapeView {
        return ShapeView;
    }

    /**
     * The associated class for the image shape view as
     * required by the composer
     */
    protected get imageViewType(): typeof ImageView {
        return ImageView;
    }

    /**
     * The associated connector view class as required by
     * Diagram Composer
     * @override
     */
    protected get connectorViewType(): typeof ConnectorView {
        return ConnectorView;
    }

    /**
     * This function gets called whenever a shapes zIndex value changes.
     * @override
     */
    protected handleZIndexChange ( shapeId: string, zIndex: number ): void {
        // NOTE: Currently nothing to be done here
    }


    protected async getWatermark() {
        return new Promise(( resolve, reject ) => {
            const svgImage = new Image();
            svgImage.src = './assets/images/logo-on-white-200X80.svg';
            svgImage.onload = () => {
                const bitmap = new Bitmap(svgImage);
                bitmap.alpha = 0.35;
                resolve( bitmap );
            };
        })
    }

    protected positionWatermark( watermark: Bitmap, diagrambounds: any ) {
        const _wmBounds = watermark.getBounds();
        // watermark.regX = _wmBounds.width / 2;
        // watermark.regY = _wmBounds.height / 2;
        if ( _wmBounds.width > diagrambounds.width ) {
            watermark.scaleX = diagrambounds.width / _wmBounds.width;
            watermark.scaleY = watermark.scaleX;
        }
        if ( _wmBounds.height > diagrambounds.height ) {
            watermark.scaleY = Math.min( diagrambounds.height / _wmBounds.height, watermark.scaleY || 1 );
            watermark.scaleX = watermark.scaleY;
        }

        const wmBounds = watermark.getTransformedBounds();
        watermark.x = diagrambounds.x + diagrambounds.width / 2 - wmBounds.width / 2;
        watermark.y = diagrambounds.y + diagrambounds.height - wmBounds.height;
        return watermark;
    }

}